import { EstimateStatus } from "@rebuild/shared";
import { useGetDashboardEstimatesQuery } from "@/hooks/queries/estimates";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useUser } from "@/hooks/useUser";
import type { Estimation } from "@/interfaces/estimation.interface";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDebounceState } from "@/hooks/useDebounceState";

const DEFAULT_PAGE_SIZE = 25;

function parsePageParam(pageParam: string | null) {
  const page = Number(pageParam);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

export const useDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, findById } = useUser();
  const { isFeatureEnabled, loading: featureFlagsLoading } = useFeatureFlags();

  const [currentProjectName, setCurrentProjectName] = useState("");
  const [currentSearchStatus, setCurrentSearchStatus] = useState<string[]>([]);
  const [currentAssignee, setCurrentAssignee] = useState<string | undefined>(undefined);

  const hasSeededAssigneeRef = useRef(false);
  useEffect(() => {
    if (hasSeededAssigneeRef.current || featureFlagsLoading) return;
    if (!isFeatureEnabled("DASHBOARD_DEFAULT_ASSIGNEE_TO_SELF")) return;
    const selfId = currentUser?.lassoUserId;
    if (!selfId) return;
    hasSeededAssigneeRef.current = true;
    setCurrentAssignee(selfId);
  }, [featureFlagsLoading, isFeatureEnabled, currentUser?.lassoUserId]);

  const pageParam = searchParams.get("page");
  const page = parsePageParam(pageParam);
  const [itemPerPage, setItemPerPage] = useState(DEFAULT_PAGE_SIZE);

  // Debounce search to avoid firing a request on every keystroke
  const debouncedSearch = useDebounceState(currentProjectName, 300);

  const setPage = useCallback(
    (nextPage: number, options?: { replace?: boolean }) => {
      const sanitizedPage = Math.max(1, nextPage);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("page", String(sanitizedPage));
      setSearchParams(nextParams, { replace: options?.replace ?? false });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (pageParam && page === 1 && pageParam !== "1") {
      setPage(1, { replace: true });
    }
  }, [page, pageParam, setPage]);

  // Reset to page 1 when any filter changes
  const prevFiltersRef = useRef({ debouncedSearch, currentSearchStatus, currentAssignee });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.debouncedSearch !== debouncedSearch ||
      prev.currentSearchStatus !== currentSearchStatus ||
      prev.currentAssignee !== currentAssignee
    ) {
      setPage(1, { replace: true });
    }
    prevFiltersRef.current = { debouncedSearch, currentSearchStatus, currentAssignee };
  }, [debouncedSearch, currentSearchStatus, currentAssignee, setPage]);

  // Build status filter for the API
  const statusFilter = useMemo(() => {
    if (currentSearchStatus.length === 0 || currentSearchStatus.includes(EstimateStatus.ALL)) {
      return undefined;
    }
    return currentSearchStatus;
  }, [currentSearchStatus]);

  const {
    data: queryResult,
    isLoading,
    isFetching,
  } = useGetDashboardEstimatesQuery({
    page,
    limit: itemPerPage,
    status: statusFilter,
    assigneeId: currentAssignee,
    search: debouncedSearch || undefined,
  });

  const estimations: Estimation[] = useMemo(() => {
    return queryResult?.data ?? [];
  }, [queryResult]);

  const total = queryResult?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / itemPerPage));

  useEffect(() => {
    if (!queryResult || page <= totalPages) return;
    setPage(totalPages, { replace: true });
  }, [page, queryResult, setPage, totalPages]);

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      setItemPerPage(pageSize);
      setPage(1, { replace: true });
    },
    [setPage],
  );

  const searchByStatus = useCallback((statuses: string[]) => {
    setCurrentSearchStatus(statuses);
  }, []);

  const searchByUser = useCallback((userId: string | undefined) => {
    setCurrentAssignee(userId);
  }, []);

  const searchByProject = useCallback((projectName: string) => {
    setCurrentProjectName(projectName);
  }, []);

  const onClickRow = useCallback(
    (estimateId: string) => {
      // Pass the already-loaded estimate to the detail view so it can render immediately
      // without refetching the metadata (avoids the empty/skeleton flash on open).
      const estimate = estimations.find((e) => e.estimateId === estimateId);
      navigate(`/estimate/${estimateId}`, {
        state: { backTo: `${location.pathname}${location.search}${location.hash}`, estimate },
      });
    },
    [estimations, location.hash, location.pathname, location.search, navigate],
  );

  const tableMeta = useMemo(() => ({ findUserById: findById }), [findById]);

  return {
    isLoading,
    isFetching,
    estimations,
    itemPerPage,
    currentAssignee,
    currentProjectName,
    currentSearchStatus,
    onClickRow,
    searchByStatus,
    searchByUser,
    searchByProject,
    tableMeta,
    // Pagination
    page,
    setPage,
    total,
    setItemPerPage: handlePageSizeChange,
  };
};
